import { Link } from "react-router-dom";
import { useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Cloud, 
  Code2, 
  Shield, 
  TrendingDown, 
  LineChart, 
  Database, 
  HeadphonesIcon,
  ArrowRight,
  Server,
  ExternalLink,
  CheckCircle2
} from "lucide-react";
import { breadcrumbSchema, usePageSeo } from "./seo";
const LOGO_URL = "/logo_suanuvem.avif";
// Cases de sucesso
const cases = [
  {
    id: "cooruja",
    name: "Cooruja",
    logo: "/logo_cooruja.avif",
    title: "Gravação de Câmeras em Nuvem com Alta Disponibilidade",
    description: "Sistema de monitoramento com gravação em nuvem, qualidade 4K, análise de tráfego e detecção preventiva de ameaças. Solução escalável com SLA de 99,8%.",
    url: "https://cooruja.seg.br/",
    services: ["DevOps & Cloud Native", "Cloud Pública e Privada", "Segurança & DevSecOps", "Observabilidade & Monitoramento", "Backup, DR & Business Continuity", "Suporte & Sustentação"],
    results: [
      "99,8% de disponibilidade garantida",
      "Gravação em qualidade HD, FHD e 4K",
      "Análise inteligente de tráfego de veículos",
      "Integração com múltiplas marcas de câmeras",
      "Infraestrutura completa gerenciada pela Sua Nuvem"
    ]
  },
  {
    id: "fincat",
    name: "FinCat",
    logo: "/logo_fincat.avif",
    title: "Plataforma Financeira Escalável e Segura",
    description: "Sistema de gestão financeira inteligente com alta performance, segurança robusta e infraestrutura cloud moderna para suportar milhares de usuários simultâneos.",
    url: "https://app.fincat.com.br/",
    services: ["DevOps & Cloud Native", "Servidor Virtual Dedicado (VDS)"],
    results: [
      "Infraestrutura VDS altamente escalável", 
      "Consultoria DevOps para otimização contínua",
      "Segurança de dados financeiros garantida",
      "Performance otimizada para operações críticas",
      "Redução de custos operacionais em 40%"
    ]
  },
  {
    id: "confiax",
    name: "Confiax Seguros",
    logo: "/logo_confiax.avif",
    title: "Plataforma de Gestão de Seguros Imobiliários",
    description: "Solução tecnológica completa para gestão de seguros imobiliários, fiança e capitalização. Plataforma web robusta com integrações complexas e alta disponibilidade para o mercado de imobiliárias.",
    url: "https://www.confiaxseguros.com.br/",
    services: ["Servidor Virtual Dedicado (VDS)"],
    results: [
      "Plataforma 100% disponível para corretoras",
      "Integração com múltiplas seguradoras",
      "Sistema de cotação em tempo real",
      "Gestão eficiente de apólices e contratos",
      "Infraestrutura escalável para crescimento"
    ]
  }
];

function CasesPage() {
  usePageSeo({
    title: "Cases de Sucesso em Cloud e DevOps | Sua Nuvem",
    description:
      "Conheça cases de clientes da Sua Nuvem com cloud, DevOps, infraestrutura escalável, alta disponibilidade, segurança e redução de custos.",
    path: "/cases",
    structuredData: [
      breadcrumbSchema([
        { name: "Início", path: "/" },
        { name: "Cases", path: "/cases" },
      ]),
    ],
  });

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-brand-dark text-white">
      {/* Hero Section */}
      <section className="pt-32 md:pt-40 pb-24 md:pb-32 px-6 md:px-12" data-testid="cases-hero">
        <div className="max-w-7xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-brand-sage hover:underline mb-8"
            data-testid="back-to-home"
          >
            <ArrowRight className="w-4 h-4 rotate-180" />
            Voltar para Início
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto mb-16"
          >
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight leading-tight mb-6">
              Cases de Sucesso
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 leading-relaxed">
              Conheça empresas que confiaram na Sua Nuvem para transformar sua infraestrutura em nuvem
            </p>
          </motion.div>
        </div>
      </section>

      {/* Cases Grid */}
      <section className="pb-24 md:pb-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto space-y-24">
          {cases.map((caseItem, index) => (
            <motion.div
              key={caseItem.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="border border-white/10 bg-brand-surface/30 rounded-xl overflow-hidden hover:border-brand-sage/50 transition-all duration-300"
              data-testid={`case-${caseItem.id}`}
            >
              <Link to={`/cases/${caseItem.id}`}>
                <div className="grid md:grid-cols-2 gap-8 cursor-pointer">
                  {/* Content */}
                  <div className="p-8 md:p-12">
                    <div className="mb-6">
                      <img 
                        src={caseItem.logo} 
                        alt={caseItem.name}
                        className="h-16 w-auto object-contain"
                        width="160"
                        height="64"
                      />
                    </div>

                    <h2 className="text-2xl md:text-3xl font-bold mb-4">
                      {caseItem.title}
                    </h2>

                    <p className="text-lg text-gray-300 mb-6 leading-relaxed">
                      {caseItem.description}
                    </p>

                    <div className="mb-6">
                      <h3 className="text-sm font-semibold text-brand-sage uppercase tracking-wider mb-3">
                        Serviços Utilizados
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {caseItem.services.map((service, i) => (
                          <span
                            key={i}
                            className="px-3 py-1 bg-brand-sage/10 border border-brand-sage/30 rounded-full text-sm text-brand-sage"
                          >
                            {service}
                          </span>
                        ))}
                      </div>
                    </div>

                    <span className="text-brand-sage font-semibold flex items-center gap-2">
                      Ver case completo <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>

                  {/* Results */}
                  <div className="bg-brand-dark/50 p-8 md:p-12 flex flex-col justify-center">
                    <h3 className="text-xl font-semibold mb-6 text-brand-sage">
                      Resultados Alcançados
                    </h3>
                    <ul className="space-y-4">
                      {caseItem.results.map((result, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <CheckCircle2 className="w-6 h-6 text-brand-sage flex-shrink-0 mt-1" />
                          <span className="text-gray-300">{result}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 md:px-12 bg-brand-surface/30">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              Quer ser nosso próximo caso de sucesso?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Entre em contato com nossos especialistas e descubra como podemos transformar sua infraestrutura.
            </p>
            <button
              onClick={(e) => {
                e.preventDefault();
                window.location.replace('/#contato');
              }}
              className="inline-block bg-brand-sage text-brand-dark px-8 py-4 rounded-md font-semibold hover:bg-opacity-90 transition-all cursor-pointer"
              data-testid="cases-cta-button"
            >
              Fale com um Especialista
            </button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default CasesPage;
export { cases };
